import { useState, useEffect, useRef } from 'react';
import { translate } from '../../../localization/translateService';
import { LANDING_HEADER_NAVIGATION_HREF } from "../../../constants/app-constants";

// Constants
const MOBILE_BREAKPOINT = 1120;

// Translation keys mapping
const TRANSLATION_KEYS = {
  tutorRanking: 'UMBRACO_LANDING.HEADER.TUTOR_RANKING',
  lecture: 'UMBRACO_LANDING.HEADER.1_1_LECTURE',
  vod: 'UMBRACO_LANDING.HEADER.VOD',
  vodSeries: 'UMBRACO_LANDING.HEADER.VOD_SERIES',
  aiTutor: 'UMBRACO_LANDING.HEADER.AI_TUTOR',
  test: 'UMBRACO_LANDING.HEADER.TEST',
  preTest: 'UMBRACO_LANDING.HEADER.PRE_TEST',
  topikTest: 'UMBRACO_LANDING.HEADER.TOPIK_TEST',
  packages: 'UMBRACO_LANDING.HEADER.PACKAGES',
  events: 'UMBRACO_LANDING.HEADER.EVENTS',
  class: 'UMBRACO_LANDING.HEADER.CLASS',
  shorts: 'UMBRACO_LANDING.HEADER.SHORTS',
};

export default function MainNavigationAi({ language, onOpenPopup, logo }) {
  // State management
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [translations, setTranslations] = useState({});
  
  // Refs
  const navRef = useRef(null);

  // Navigation configuration
  const getNavigationItems = () => [
    {
      label: translations?.tutorRanking,
      href: LANDING_HEADER_NAVIGATION_HREF.RANKING_LEADERBOARD,
    },
    {
      label: translations?.lecture,
      href: LANDING_HEADER_NAVIGATION_HREF.REAL_TIME_LECTURES,
    },
    {
      label: translations?.class ?? "Class",
      href: LANDING_HEADER_NAVIGATION_HREF.CLASS,
    },
    {
      label: translations?.shorts ?? "Shorts",
      href: LANDING_HEADER_NAVIGATION_HREF.SHORTS,
    },
    {
      label: translations?.vod,
      dropdown: [
        { label: translations?.vod, href: LANDING_HEADER_NAVIGATION_HREF.VOD },
        { label: translations?.vodSeries, href: LANDING_HEADER_NAVIGATION_HREF.VOD_SERIES }
      ]
    },
    {
      label: translations?.aiTutor,
      href: LANDING_HEADER_NAVIGATION_HREF.AI_TUTOR,
    },
    {
      label: translations?.test,
      dropdown: [
        { label: translations?.preTest, href: LANDING_HEADER_NAVIGATION_HREF.PRE_TEST },
        { label: translations?.topikTest, href: LANDING_HEADER_NAVIGATION_HREF.TOPIK_TEST }
      ]
    },
    // Temporarily hide Packages menu per request
    // {
    //   label: translations?.packages,
    //   href: LANDING_HEADER_NAVIGATION_HREF.PACKAGES
    // },
    {
      label: translations?.events,
      href: LANDING_HEADER_NAVIGATION_HREF.EVENTS,
    },
  ];

  // Load translations
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const translationData = {};
        
        for (const [key, translationKey] of Object.entries(TRANSLATION_KEYS)) {
          const fallbackValue = getFallbackValue(key);
          translationData[`${key}`] = await translate(translationKey, false, fallbackValue);
        }
        
        setTranslations(translationData);
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    };

    loadTranslations();
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > MOBILE_BREAKPOINT) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Manage body scroll
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Event handlers
  const handleDropdownToggle = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleNavItemClick = (item) => {
    setOpenDropdown(null);
    closeMobileMenu();
    
    if (item.openPopup) {
      onOpenPopup(item.openPopup);
    }
  };

  const isActiveRoute = (item) => {
    return window.location.pathname.includes(item.href) || item?.dropdown?.find((dropdownItem) => window.location.pathname.includes(dropdownItem?.href));
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Helper functions
  const getFallbackValue = (key) => {
    const fallbacks = {
      tutorRanking: 'Tutor Ranking',
      lecture: '1:1 Lecture',
      vod: 'VOD',
      vodSeries: 'VOD Series',
      aiTutor: 'AI Tutor',
      test: 'Test',
      preTest: 'Pre-test',
      topikTest: 'TOPIK test',
      packages: 'Packages',
      events: 'Events',
      class: 'Courses',
      shorts: 'Shorts',
    };
    return fallbacks[key] || key;
  };

  const renderDesktopMenuItem = (item) => {
    if (item.dropdown) {
      return (
        <>
          <a
            className={`nav-link has-dropdown ${
              openDropdown === item.label || isActiveRoute(item) ? 'active' : ''
            }`}
            onClick={() => handleDropdownToggle(item.label)}
          >
            {item.label}
          </a>
          <div className={`menudropdown-menu ${openDropdown === item.label ? 'show' : ''}`}>
            {item.dropdown.map((dropdownItem) => (
              <a
                key={dropdownItem.label}
                href={dropdownItem.openPopup ? '#' : `${language}${dropdownItem.href}`}
                className="menudropdown-item"
                onClick={() => handleNavItemClick(dropdownItem)}
              >
                {dropdownItem.label}
              </a>
            ))}
          </div>
        </>
      );
    }

    return (
      <a
        href={item.openPopup ? '#' : `${language}${item.href}`}
        className={`nav-a ${isActiveRoute(item) ? 'active' : ''}`}
        onClick={() => handleNavItemClick(item)}
      >
        {item.label}
      </a>
    );
  };

  const renderMobileMenuItem = (item) => {
    if (item.dropdown) {
      return item.dropdown.map((dropdownItem) => (
        <li key={dropdownItem.label} className="mobile-nav-item">
          <a
            href={dropdownItem.openPopup ? '#' : `${language}${dropdownItem.href}`}
            className="mobile-nav-a"
            onClick={() => handleNavItemClick(dropdownItem)}
          >
            {dropdownItem.label}
          </a>
        </li>
      ));
    }

    return (
      <li key={item.label} className="mobile-nav-item">
        <a
          href={item.openPopup ? '#' : `${language}${item.href}`}
          className="mobile-nav-a"
          onClick={() => handleNavItemClick(item)}
        >
          {item.label}
        </a>
      </li>
    );
  };

  const navigationItems = getNavigationItems();

  return (
    <>
      <nav className="nav-container w-full" ref={navRef}>
        <div className="nav-wrapper pl-4">
          <div className="nav-bar">
            {/* Brand Logo */}
            <a href={language} className="no-underline">
              <img 
                src={logo} 
                alt="Dot Korean Logo" 
                className="header-logo m-0 cursor-pointer" 
              />
            </a>

            {/* Desktop Navigation */}
            <ul className="nav-menu">
              {navigationItems.map((item) => (
                <li key={item.label} className="nav-item">
                  {renderDesktopMenuItem(item)}
                </li>
              ))}
            </ul>

            {/* Mobile Menu Toggle */}
            <button
              className="mobile-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <i className={`pi ${mobileMenuOpen ? 'pi-times' : 'pi-bars'}`} />
            </button>
          </div>

          {/* Mobile Navigation */}
          <div className={`mobile-menu ${mobileMenuOpen ? 'show' : ''}`}>
            <ul className="mobile-nav-list">
              {navigationItems.map(renderMobileMenuItem)}
            </ul>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      <div
        className={`mobile-overlay ${mobileMenuOpen ? 'show' : ''}`}
        onClick={closeMobileMenu}
      />
    </>
  );
}
